import React, { useEffect, useState } from 'react';
import { PALETTE } from '../../types';
import { useSessionStore } from '../../store/sessionStore';
import ProcessCard from './ProcessCard';
import SaveSection from './SaveSection';
import TutorialSection from './TutorialSection';

interface SidebarProps {
  svgRef: React.RefObject<SVGSVGElement | null>;
  onSegmentEdit: (procId: number, segIdx: number) => void;
  onSegmentHover?: (procId: number, segIdx: number, hovered: boolean) => void;
  hoveredSegmentKey?: string | null;
}

const Sidebar: React.FC<SidebarProps> = ({
  svgRef,
  onSegmentEdit,
  onSegmentHover,
  hoveredSegmentKey = null,
}) => {
  const PROCESS_PANEL_DEFAULT_WIDTH = 340;
  const PROCESS_PANEL_MAXIMIZED_WIDTH = 560;
  const PROCESS_PANEL_MINIMIZED_WIDTH = 400;

  const processes = useSessionStore(s => s.processes);
  const nodes = useSessionStore(s => s.nodes);
  const edges = useSessionStore(s => s.edges);
  const activeProcessId = useSessionStore(s => s.activeProcessId);
  const hint = useSessionStore(s => s.hint);
  const createProcess = useSessionStore(s => s.createProcess);
  const activeColor = useSessionStore(s => s.activeColor);
  const setActiveColor = useSessionStore(s => s.setActiveColor);
  const reorderProcess = useSessionStore(s => s.reorderProcess);

  const [procName, setProcName] = useState('');
  const [draggingProcId, setDraggingProcId] = useState<number | null>(null);
  const [dragOverProcId, setDragOverProcId] = useState<number | null>(null);
  const [isProcessPanelCollapsed, setIsProcessPanelCollapsed] = useState(false);
  const [isProcessPanelMinimized, setIsProcessPanelMinimized] = useState(false);
  const [isProcessPanelMaximized, setIsProcessPanelMaximized] = useState(false);
  const [panelPosition, setPanelPosition] = useState({ x: 16, y: 130 });
  const [isPanelDragging, setIsPanelDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [pseudocodeExported, setPseudocodeExported] = useState(false);
  const [pngExported, setPngExported] = useState(false);
  const [tutorialVisible, setTutorialVisible] = useState(true);
  const [showCongratsPopup, setShowCongratsPopup] = useState(false);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);

  const handleCreate = () => {
    const name = procName.trim() || `Proceso ${processes.length + 1}`;
    createProcess(name, activeColor);
    setProcName('');
    const ni = (PALETTE.indexOf(activeColor as any) + 1) % PALETTE.length;
    setActiveColor(PALETTE[ni]);
  };

  const divider = <div style={{ height: 1, background: '#ede9e0', flexShrink: 0 }} />;

  const handleDropAt = (targetProcId: number) => {
    if (draggingProcId === null || draggingProcId === targetProcId) return;
    const fromIdx = processes.findIndex(p => p.id === draggingProcId);
    const toIdx = processes.findIndex(p => p.id === targetProcId);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    reorderProcess(fromIdx, toIdx);
  };

  useEffect(() => {
    const pseudo = window.localStorage.getItem('pm_tutorial_pseudocode') === '1';
    const png = window.localStorage.getItem('pm_tutorial_png') === '1';
    const hidden = window.localStorage.getItem('pm_tutorial_hidden') === '1';
    const completed = window.localStorage.getItem('pm_tutorial_completed') === '1';
    setPseudocodeExported(pseudo);
    setPngExported(png);
    setTutorialVisible(!hidden);
    setTutorialCompleted(completed);
  }, []);

  useEffect(() => {
    if (!isPanelDragging) return;

    const panelWidth = isProcessPanelMinimized
      ? PROCESS_PANEL_MINIMIZED_WIDTH
      : isProcessPanelMaximized
        ? PROCESS_PANEL_MAXIMIZED_WIDTH
        : PROCESS_PANEL_DEFAULT_WIDTH;
    const panelHeight = isProcessPanelMinimized || isProcessPanelCollapsed
      ? 48
      : isProcessPanelMaximized
        ? window.innerHeight - 36
        : window.innerHeight * 0.65;

    const onMouseMove = (event: MouseEvent) => {
      const newX = Math.max(0, Math.min(window.innerWidth - panelWidth, event.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - panelHeight, event.clientY - dragOffset.y));
      setPanelPosition({ x: newX, y: newY });
    };

    const onMouseUp = () => setIsPanelDragging(false);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [
    dragOffset.x,
    dragOffset.y,
    isPanelDragging,
    isProcessPanelCollapsed,
    isProcessPanelMaximized,
    isProcessPanelMinimized,
    PROCESS_PANEL_DEFAULT_WIDTH,
    PROCESS_PANEL_MAXIMIZED_WIDTH,
    PROCESS_PANEL_MINIMIZED_WIDTH,
  ]);

  useEffect(() => {
    const panelWidth = isProcessPanelMinimized
      ? PROCESS_PANEL_MINIMIZED_WIDTH
      : isProcessPanelMaximized
        ? PROCESS_PANEL_MAXIMIZED_WIDTH
        : PROCESS_PANEL_DEFAULT_WIDTH;
    const panelHeight = isProcessPanelMinimized || isProcessPanelCollapsed
      ? 48
      : isProcessPanelMaximized
        ? window.innerHeight - 36
        : window.innerHeight * 0.65;

    setPanelPosition(prev => ({
      x: Math.max(0, Math.min(window.innerWidth - panelWidth, prev.x)),
      y: Math.max(0, Math.min(window.innerHeight - panelHeight, prev.y)),
    }));
  }, [
    isProcessPanelCollapsed,
    isProcessPanelMaximized,
    isProcessPanelMinimized,
    PROCESS_PANEL_DEFAULT_WIDTH,
    PROCESS_PANEL_MAXIMIZED_WIDTH,
    PROCESS_PANEL_MINIMIZED_WIDTH,
  ]);

  const handlePseudocodeExport = () => {
    setPseudocodeExported(true);
    window.localStorage.setItem('pm_tutorial_pseudocode', '1');
  };

  const handlePngExport = () => {
    setPngExported(true);
    window.localStorage.setItem('pm_tutorial_png', '1');
  };

  const resetTutorial = () => {
    setPseudocodeExported(false);
    setPngExported(false);
    setTutorialCompleted(false);
    setShowCongratsPopup(false);
    setTutorialVisible(true);
    window.localStorage.removeItem('pm_tutorial_pseudocode');
    window.localStorage.removeItem('pm_tutorial_png');
    window.localStorage.removeItem('pm_tutorial_completed');
    window.localStorage.removeItem('pm_tutorial_hidden');
  };

  const closeTutorial = () => {
    setTutorialVisible(false);
    window.localStorage.setItem('pm_tutorial_hidden', '1');
  };

  const openTutorial = () => {
    setTutorialVisible(true);
    window.localStorage.removeItem('pm_tutorial_hidden');
  };

  const handleTutorialCompletionChange = (done: boolean) => {
    if (!done) return;
    if (tutorialCompleted) return;

    setTutorialCompleted(true);
    setShowCongratsPopup(true);
    setTutorialVisible(false);
    window.localStorage.setItem('pm_tutorial_completed', '1');
    window.localStorage.setItem('pm_tutorial_hidden', '1');
  };

  return (
    <>
      <div style={{
        width: 300,
        height: '100%',
        background: '#fff',
        borderLeft: '1px solid #e2ddd4',
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 16px',
        gap: 14,
        overflow: 'hidden',
      }}>
        {divider}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          <p style={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', letterSpacing: '.07em' }}>Nuevo proceso</p>
          <input
            value={procName}
            onChange={e => setProcName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            placeholder="ej. Validación presupuesto"
            style={{
              border: '1px solid #ddd', borderRadius: 6, padding: '8px 10px',
              fontFamily: "'Caveat',cursive", fontSize: 16, color: '#333',
              background: '#fafaf8', outline: 'none', width: '100%',
            }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            {PALETTE.map(c => (
              <div key={c} onClick={() => setActiveColor(c)} style={{
                width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                background: c, flexShrink: 0,
                border: `2px solid ${activeColor === c ? '#333' : 'transparent'}`,
                transform: activeColor === c ? 'scale(1.2)' : 'scale(1)',
                transition: 'border .15s, transform .15s',
              }} />
            ))}
          </div>
          <button onClick={handleCreate} style={{
            padding: 9, background: '#2d7a50', color: 'white', border: 'none',
            borderRadius: 7, fontFamily: "'Caveat',cursive", fontSize: 16,
            fontWeight: 700, cursor: 'pointer',
          }}>
            + Iniciar proceso
          </button>
        </div>

        <div style={{
          background: '#f0faf5', border: '1px solid #c5e8d4', borderRadius: 8,
          padding: '9px 12px', fontFamily: "'Caveat',cursive", fontSize: 15,
          color: '#2d7a50', minHeight: 40, display: 'flex', alignItems: 'center', flexShrink: 0,
        }}>
          {hint}
        </div>

        {divider}
        <SaveSection
          svgRef={svgRef}
          onExportPseudocode={handlePseudocodeExport}
          onExportPNG={handlePngExport}
        />
      </div>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: 18,
          right: 316,
          zIndex: 35,
          width: 320,
          maxHeight: 'calc(100vh - 36px)',
          overflowY: 'auto',
          pointerEvents: 'auto',
        }}
      >
        {tutorialVisible ? (
          <TutorialSection
            nodes={nodes}
            processes={processes}
            edges={edges}
            pseudocodeExported={pseudocodeExported}
            pngExported={pngExported}
            onReset={resetTutorial}
            onClose={closeTutorial}
            onCompletionChange={handleTutorialCompletionChange}
          />
        ) : (
          <button
            type="button"
            onClick={openTutorial}
            style={{
              border: '1px dashed #d9d1c3',
              background: '#fffaf0',
              borderRadius: 8,
              padding: '7px 10px',
              color: '#7d7365',
              fontFamily: "'DM Mono',monospace",
              fontSize: 10,
              letterSpacing: '.02em',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
            }}
          >
            📘 Mostrar tutorial
          </button>
        )}
      </div>

      {showCongratsPopup && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Tutorial completado"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            zIndex: 80,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 16,
          }}
          onClick={() => setShowCongratsPopup(false)}
        >
          <div
            onClick={event => event.stopPropagation()}
            style={{
              width: 'min(420px, 100%)',
              background: '#fff',
              border: '1px solid #e8e1d5',
              borderRadius: 12,
              boxShadow: '0 14px 40px rgba(0, 0, 0, 0.18)',
              padding: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <p style={{ margin: 0, fontFamily: "'DM Mono',monospace", fontSize: 11, color: '#9b927f', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              ¡Tutorial completado!
            </p>
            <p style={{ margin: 0, fontFamily: "'Caveat',cursive", fontSize: 24, color: '#2d7a50', lineHeight: 1.2 }}>
              🎉 ¡Buen trabajo! Ya completaste el flujo base.
            </p>
            <p style={{ margin: 0, fontFamily: "'DM Mono',monospace", fontSize: 11, color: '#6f665a', lineHeight: 1.5 }}>
              El panel del tutorial se cerró automáticamente. Puedes volver a abrirlo con el botón "Mostrar tutorial" cuando quieras.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowCongratsPopup(false)}
                style={{
                  border: 'none',
                  background: '#2d7a50',
                  color: '#fff',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontFamily: "'DM Mono',monospace",
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          position: 'fixed',
          top: panelPosition.y,
          left: panelPosition.x,
          width: isProcessPanelMinimized
            ? PROCESS_PANEL_MINIMIZED_WIDTH
            : isProcessPanelMaximized
              ? PROCESS_PANEL_MAXIMIZED_WIDTH
              : PROCESS_PANEL_DEFAULT_WIDTH,
          maxHeight: (isProcessPanelMinimized || isProcessPanelCollapsed) ? 48 : isProcessPanelMaximized ? 'calc(100vh - 36px)' : '65vh',
          background: '#fff',
          border: '1px solid #e2ddd4',
          borderRadius: 12,
          boxShadow: '0 10px 26px rgba(0,0,0,0.1)',
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          onMouseDown={event => {
            event.preventDefault();
            event.stopPropagation();
            setIsPanelDragging(true);
            setDragOffset({
              x: event.clientX - panelPosition.x,
              y: event.clientY - panelPosition.y,
            });
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: '10px 12px',
            borderBottom: isProcessPanelCollapsed ? 'none' : '1px solid #eee7db',
            background: '#faf7f2',
            cursor: isPanelDragging ? 'grabbing' : 'grab',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ opacity: 0.5, userSelect: 'none' }}>⋮⋮</span>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: '#7f766a', textTransform: 'uppercase', letterSpacing: '.08em' }}>
              {isProcessPanelMinimized ? 'Procesos minimizado' : `Procesos (${processes.length})`}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                setIsProcessPanelMinimized(prev => !prev);
              }}
              style={{
                border: 'none',
                background: '#fff',
                borderRadius: 999,
                width: 26,
                height: 26,
                cursor: 'pointer',
                color: '#766f64',
                fontSize: 14,
              }}
              title={isProcessPanelMinimized ? 'Restaurar panel' : 'Minimizar panel'}
            >
              {isProcessPanelMinimized ? '↗' : '—'}
            </button>
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                setIsProcessPanelMaximized(prev => !prev);
                setIsProcessPanelMinimized(false);
              }}
              style={{
                border: 'none',
                background: '#fff',
                borderRadius: 999,
                width: 26,
                height: 26,
                cursor: 'pointer',
                color: '#766f64',
                fontSize: 13,
              }}
              title={isProcessPanelMaximized ? 'Restaurar tamaño normal' : 'Maximizar panel'}
            >
              {isProcessPanelMaximized ? '❐' : '□'}
            </button>
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                setIsProcessPanelCollapsed(prev => !prev);
                setIsProcessPanelMinimized(false);
              }}
              style={{
                border: 'none',
                background: '#fff',
                borderRadius: 999,
                width: 26,
                height: 26,
                cursor: 'pointer',
                color: '#766f64',
                fontSize: 14,
              }}
              title={isProcessPanelCollapsed ? 'Desplegar procesos' : 'Plegar procesos'}
            >
              {isProcessPanelCollapsed ? '▾' : '▴'}
            </button>
          </div>
        </div>

        {!isProcessPanelCollapsed && !isProcessPanelMinimized && (
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: 12, minHeight: 110 }}>
            {processes.length === 0
              ? <p style={{ fontFamily: "'Caveat',cursive", fontSize: 15, color: '#ccc', textAlign: 'center', marginTop: 16 }}>
                Los procesos aparecerán aquí
              </p>
              : processes.map((proc, index) => (
                <div
                  key={proc.id}
                  draggable
                  onDragStart={e => {
                    e.dataTransfer.setData('text/plain', String(proc.id));
                    e.dataTransfer.effectAllowed = 'move';
                    setDraggingProcId(proc.id);
                  }}
                  onDragOver={e => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (dragOverProcId !== proc.id) setDragOverProcId(proc.id);
                  }}
                  onDrop={e => {
                    e.preventDefault();
                    handleDropAt(proc.id);
                    setDraggingProcId(null);
                    setDragOverProcId(null);
                  }}
                  onDragEnd={() => {
                    setDraggingProcId(null);
                    setDragOverProcId(null);
                  }}
                  style={{ cursor: 'grab' }}
                >
                  <ProcessCard
                    proc={proc}
                    order={index + 1}
                    isActive={proc.id === activeProcessId}
                    onSegmentEdit={onSegmentEdit}
                    onSegmentHover={onSegmentHover}
                    hoveredSegmentKey={hoveredSegmentKey}
                    isDragTarget={dragOverProcId === proc.id && draggingProcId !== null && draggingProcId !== proc.id}
                  />
                </div>
              ))
            }
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
